import React from 'react';

export interface PromoItem {
  id: string;
  cover: string;
  title: string;
  description: string;
  link?: string;
  tag?: string;
}

interface PromoCardProps {
  item: PromoItem;
  active?: boolean;
  onClick?: (item: PromoItem) => void;
  width?: number;   // 卡片宽度，默认 500
  height?: number;  // 卡片高度，默认 280
  side?: 'left' | 'right'; // 非中间卡片靠近中间一侧的阴影方向
}

export const PromoCard = React.memo<PromoCardProps>(({ item, active = false, onClick, width = 500, height = 280, side }) => {
  // 流式自适应收尾：卡片盒体由容器实测宽驱动（PromoCarousel 传入 width），
  // 内部文字/标签/内边距按「卡片宽/500」等比换算并设可读下限。
  // 替代旧的视口单位（标题 clamp(…,3vw,…)、标签/内边距 md: 断点）——那是整页
  // 缩放时代「vw ∝ 卡片尺寸」假设的残留；容器实测改造后卡片随容器、文字却随
  // 视口，比例会随侧栏折叠与窗口宽度漂移。
  // 基准值即设计稿 500×280 下的呈现：pad 24 / 标题 24 / 描述 14 / 标签芯片
  // 内缩 16、内边距 12×4、字号 12。
  const k = width / 500;
  const pad = Math.max(10, Math.round(24 * k));
  const titleSize = Math.max(10, Math.round(24 * k));
  const titleGap = Math.max(2, Math.round(8 * k));
  const descSize = Math.max(8, Math.round(14 * k));
  const chipInset = Math.max(6, Math.round(16 * k));
  const chipPadX = Math.max(6, Math.round(12 * k));
  const chipPadY = Math.max(2, Math.round(4 * k));
  const chipFont = Math.max(8, Math.round(12 * k));

  return (
    <div
      onClick={() => onClick?.(item)}
      className={`group relative rounded-2xl overflow-hidden cursor-pointer bg-zinc-900 border transition-colors ${
        active
          ? 'border-transparent'
          : 'border-white/5'
      }`}
      style={{
        width: `${width}px`,
        height: `${height}px`,
      }}
    >
      <img
        src={item.cover}
        alt={item.title}
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        style={{ userSelect: 'none' }}
      />
      <div
        className={`absolute inset-0 pointer-events-none transition-opacity duration-500 ${
          side === 'left'
            ? 'bg-gradient-to-r from-black/40 to-transparent'
            : 'bg-gradient-to-l from-black/40 to-transparent'
        }`}
        style={{ opacity: side ? 1 : 0 }}
      />
      {item.tag && (
        <div
          className="absolute rounded-full bg-white/10 backdrop-blur-md border border-white/10 font-medium text-white"
          style={{
            top: chipInset,
            left: chipInset,
            padding: `${chipPadY}px ${chipPadX}px`,
            fontSize: `${chipFont}px`,
            lineHeight: 1.2,
          }}
        >
          {item.tag}
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 text-center" style={{ padding: pad }}>
        <h3
          className="font-semibold text-white tracking-tight"
          style={{ fontSize: `${titleSize}px`, marginBottom: titleGap, lineHeight: 1.25 }}
        >
          {item.title}
        </h3>
        <p className="text-zinc-300 line-clamp-2" style={{ fontSize: `${descSize}px`, lineHeight: 1.4 }}>
          {item.description}
        </p>
      </div>
    </div>
  );
});
